import * as React from "react";
import { Filter, PropertyFiltersGroupProps, PropertyFiltersProps } from "../../typings";
import { UitkExpandoList, UitkExpandoListItem } from "uitk-react-expando";
import { UitkSecondaryButton, UitkSecondaryFloatingButton } from "uitk-react-button";
import { UitkNotificationBadge1 } from "uitk-react-badge";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { PropertyFilterInDialog } from "../../dialog/PropertyFiltersDialog";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { PROPERTY_FILTERS } from "components/flexComponents/PropertyFilters/PropertyFilters";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { UitkCardAloha } from "uitk-react-cards";
import { DesktopPropertyFiltersStores, WithApplyProp } from "./types";

type ExpandoPropertyFiltersProps = Pick<DesktopPropertyFiltersStores, "propertyFilters"> & WithApplyProp;

export const ExpandoPropertyFilters = withStores("propertyFilters")(
  observer(({ propertyFilters, apply }: ExpandoPropertyFiltersProps) => {
    const { formatText } = useLocalization();
    const [isApplyButtonHidden, setIsApplyButtonHidden] = React.useState<boolean>(false);

    const numberOfSelectedFilters = propertyFilters.getNumberOfSelectedFilters();

    const showStickyButton = isApplyButtonHidden && numberOfSelectedFilters > 0;

    const onApply: React.FormEventHandler = (e) => {
      e.preventDefault();
      apply(showStickyButton ? "apply.sticky" : "apply");
    };

    React.useEffect(() => {
      const applyContainer = document.querySelector(".DesktopPropertyFilters .applyContainer");
      if (!applyContainer) return;

      const intersectionObserver = new IntersectionObserver(
        ([e]) => {
          setIsApplyButtonHidden(e.intersectionRatio < 1);
        },
        {
          threshold: [1],
        }
      );

      intersectionObserver.observe(applyContainer);
    }, []);

    const ApplyButton = showStickyButton ? UitkSecondaryFloatingButton : UitkSecondaryButton;
    const ButtonWrapper = showStickyButton ? UitkScrim : UitkLayoutFlexItem;

    return (
      <UitkSpacing padding={{ blockstart: "four" }}>
        <UitkCardAloha className="DesktopPropertyFilters" overflow={true}>
          <form onSubmit={onApply}>
            <UitkSpacing padding={{ blockstart: "two", blockend: "six", inlinestart: "four" }}>
              <UitkHeading level={2} size={500} className="desktopHeader uitk-type-bold">
                {formatText("propertyfilters.filterBy")}
              </UitkHeading>
            </UitkSpacing>
            <UitkSpacing padding={{ inlineend: "two" }}>
              <UitkExpandoList>
                {propertyFilters.getListOfFilters().map((filter, index) => (
                  <PropertyFiltersGroup key={`${filter.name}-${index}`} filter={filter} />
                ))}
              </UitkExpandoList>
            </UitkSpacing>
            <UitkSpacing padding={{ inline: "twelve" }}>
              <UitkLayoutFlex
                className={`applyContainer ${showStickyButton ? "sticky" : ""}`}
                alignItems="center"
                justifyContent="center"
              >
                <UitkLayoutFlexItem>
                  <ButtonWrapper className="applyWrapper" type={UitkScrimType.BUTTON}>
                    <ApplyButton
                      type="submit"
                      className="applyButton"
                      disabled={!numberOfSelectedFilters}
                      text={formatText("propertyfilters.apply")}
                      isFullWidth={true}
                    >
                      <UitkNotificationBadge1 text={String(numberOfSelectedFilters)} />
                    </ApplyButton>
                  </ButtonWrapper>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkSpacing>
          </form>
        </UitkCardAloha>
      </UitkSpacing>
    );
  })
);

/**
 * Represents a filter group (e.g. Popular Filters)
 */
const PropertyFiltersGroup = (props: PropertyFiltersGroupProps) => {
  const { filter } = props;
  const track = useClickTracker();

  const [isOpen, setIsOpen] = React.useState(filter?.name === "popularFilter");

  if (!filter) {
    return null;
  }

  const callback = (filterNameAndShowMoreOrLess: string) => {
    if (filterNameAndShowMoreOrLess.includes(".ShowMore")) {
      setIsOpen(true);
    }

    if (filterNameAndShowMoreOrLess.includes(".ShowLess")) {
      setIsOpen(false);
    }

    const flexTracking: FlexTrackingInfo = {
      moduleName: PROPERTY_FILTERS,
      rfrr: filterNameAndShowMoreOrLess, // e.g. stars.ShowMore
      action: Action.CLICK,
    };
    sendDelayedClickstreamTrackEvent(flexTracking, track);
  };

  return (
    <UitkExpandoListItem
      expandoTitle={<PropertyFiltersGroupTitle {...filter} isOpen={isOpen} />}
      expandoDescription={<PropertyFilterInDialog filter={filter} withoutHeader={true} />}
      analytics={{
        id: filter!.name,
        callback,
      }}
      isVisible={filter!.name === "popularFilter"}
    />
  );
};

interface PropertyFiltersGroupTitleProps
  extends Pick<Filter, "label" | "name">,
    Pick<PropertyFiltersProps, "propertyFilters"> {
  isOpen: boolean;
}

const PropertyFiltersGroupTitle = withStores("propertyFilters")(
  ({ label, name, isOpen, propertyFilters }: PropertyFiltersGroupTitleProps) => {
    if (!label || !name) {
      return null;
    }

    const totalFilters = propertyFilters.getNumberOfSelectedFiltersPerCategory(name);
    const showBadge = totalFilters > 0 && !isOpen;

    return (
      <UitkLayoutFlex justifyContent="space-between">
        <UitkLayoutFlexItem>
          <div>{label}</div>
        </UitkLayoutFlexItem>
        {showBadge && (
          <UitkLayoutFlexItem>
            <div>
              <UitkNotificationBadge1 text={String(totalFilters)} />
            </div>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    );
  }
);
